import classNames from 'classnames'
import { AuthenticationContext } from 'hooks/auth'
import { useTranslationTyped } from 'i18n/i18n'
import { useContext, useMemo, useState, useEffect } from 'react'

// How long a spinner must stay mounted before we treat it as really shown. A
// spinner that mounts and unmounts inside this window was never really visible,
// so it fires no events. Matches DelayedSpinner's default delay.
const SPINNER_SHOWN_DELAY_MS = 200

// How long a spinner may stay on screen before we treat it as stuck.
const SPINNER_STUCK_MS = 10_000

// Fires <prefix>_shown / _hidden / _stuck, each enriched with `reason`.
// Telemetry is opt-in: with no `reason` it does nothing. It reads the auth
// context directly rather than useRepository() so a spinner rendered without a
// provider (e.g. in tests) no-ops instead of throwing.
function useSpinnerTelemetry(eventPrefix: string, reason?: string) {
  const repository = useContext(AuthenticationContext)?.repository

  useEffect(() => {
    if (!reason || !repository) return

    // Only count the spinner as shown once it survives the delay. A quick
    // mount/unmount fires nothing, and we only fire hidden if shown fired.
    let shown = false

    const shownTimer = setTimeout(() => {
      shown = true
      repository.logEvent(`${eventPrefix}_shown`, { reason })
    }, SPINNER_SHOWN_DELAY_MS)

    const stuckTimer = setTimeout(() => {
      repository.logEvent(`${eventPrefix}_stuck`, { reason })
    }, SPINNER_STUCK_MS)

    return () => {
      clearTimeout(shownTimer)
      clearTimeout(stuckTimer)
      if (shown) {
        repository.logEvent(`${eventPrefix}_hidden`, { reason })
      }
    }
  }, [repository, reason, eventPrefix])
}

type SpinnerProps = {
  size?: number
  thickness?: number
  className?: string
  // When set, fires spinner_shown / _hidden / _stuck enriched with this reason.
  reason?: string
}

export function Spinner({ size, thickness, className, reason }: SpinnerProps) {
  useSpinnerTelemetry('spinner', reason)

  const circleStyle = useMemo(() => {
    const thicknessOrDefault = 8 * (8 / (thickness || 8))
    let circleStyle
    if (size) {
      circleStyle = {
        width: `${size}rem`,
        height: `${size}rem`,
        borderWidth: `${size / thicknessOrDefault}rem`,
      }
    }
    return circleStyle
  }, [size, thickness])

  return (
    <span
      className={classNames('spinner', className)}
      style={circleStyle}
    ></span>
  )
}

export function DelayedSpinner({
  size,
  thickness,
  className,
  reason,
  delayMs = 200,
}: SpinnerProps & { delayMs?: number }) {
  const [showSpinner, setShowSpinner] = useState(false)

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowSpinner(true)
    }, delayMs)

    return () => clearTimeout(timer)
  }, [delayMs])

  if (!showSpinner) {
    return null
  }

  return (
    <Spinner
      size={size}
      thickness={thickness}
      className={className}
      reason={reason}
    />
  )
}

export function SpinnerWithLogo({
  className,
  reason,
}: {
  className?: string
  reason?: string
}) {
  const { tt } = useTranslationTyped()
  return (
    <div
      className="relative flex flex-col items-center justify-center"
      aria-live="polite"
      role="status"
    >
      <Spinner size={6} thickness={6} className={className} reason={reason} />
      <img
        src="/assets/images/logo.png"
        width={64}
        className="absolute"
        alt=""
      />
      <span className="sr-only">{tt.common.loading_and_dots()}</span>
    </div>
  )
}

export function FullPageSpinner({
  reason,
  className,
}: {
  // Why this spinner is on screen. Enriches every full_page_spinner_* event.
  reason: string
  className?: string
}) {
  const { tt } = useTranslationTyped()

  // Fires full_page_spinner_* itself; the inner Spinner stays silent (no reason
  // passed down) so we don't double-count.
  useSpinnerTelemetry('full_page_spinner', reason)

  return (
    <div
      className="fixed flex h-screen w-screen flex-col items-center justify-center"
      aria-live="polite"
      role="status"
    >
      <SpinnerWithLogo className={className} />
      <span className="sr-only">{tt.common.loading_and_dots()}</span>
    </div>
  )
}

export function SpinnerCentered(props: SpinnerProps) {
  const { tt } = useTranslationTyped()
  return (
    <div
      className="flex items-center justify-center"
      aria-live="polite"
      role="status"
    >
      <Spinner {...props} />
      <span className="sr-only">{tt.common.loading_and_dots()}</span>
    </div>
  )
}
